import { Button, Result, Spin } from "antd";
import axios from "axios";
import qs from "query-string";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import {
  CAPITALONE_ORG_IDS,
  PAYONEER_ORG_IDS,
  REVOLUT_ORG_IDS,
} from "./constants";
import { useLinkTracker } from "./hooks/useLinkTracker";
import { captureException } from "./sentry";
import Store from "./stateStore";
import {
  getBackendUrl,
  getBackendWsUrl,
  handleClose,
  sendConnectionSuccessMetric,
} from "./utils";

// Testing for Payoneer first because I am scared this will break something, e.g. postMessage does not fire fast enough
// and we close the window too quickly
// Preferred UX is the popup closes immediately but I want to roll out slowly
// Rollout - Parafin, Parker
function shouldCloseImmediatelyGate(config: { orgId?: string }) {
  let { orgId } = config;
  try {
    const localStorageOrgId = localStorage.getItem("rutterOrgId");
    orgId = localStorageOrgId ?? orgId;
  } catch (e) {
    captureException(e);
  }
  if (orgId && [...PAYONEER_ORG_IDS, ...REVOLUT_ORG_IDS].includes(orgId)) {
    return true;
  }

  return false;
}

export function isCapitalOneGate() {
  const localStorageOrgId = localStorage.getItem("rutterOrgId");
  return CAPITALONE_ORG_IDS.includes(localStorageOrgId ?? "");
}

function LinkComplete() {
  useLinkTracker({ result: "success" });
  const history = useHistory();
  const { search } = history.location;
  const parsedSearch = qs.parse(search);

  const store = Store.useStore();

  const { origin, nonce, public_token, force_relay } = parsedSearch;
  const shouldUseRelay = force_relay === "true" || !window.opener;
  const capitalOnePassedOrigin =
    localStorage.getItem("parentWindowOrigin") ?? store.get("origin");
  const connectionId =
    localStorage.getItem("rutterItemId") ?? store.get("itemId");
  const orgId = store.get("orgId");

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    sendConnectionSuccessMetric();
  }, []);

  React.useEffect(() => {
    if (isCapitalOneGate()) {
      // capital one is special we want to send messages no matter what
      if (window?.opener) {
        try {
          console.info("Emitting message to opener", {
            publicToken: public_token,
            nonce,
            type: "SUCCESS",
            connectionid: connectionId,
            origin: capitalOnePassedOrigin,
          });
          window.opener.postMessage(
            JSON.stringify({
              publicToken: public_token,
              nonce,
              type: "SUCCESS",
              connectionid: connectionId,
            }),
            capitalOnePassedOrigin,
          );
        } catch (e) {
          captureException(e);
          setError("Unable to authenticate.");
        } finally {
          setLoading(false);
        }
      }
    }

    if (public_token && nonce && origin) {
      // This means it was opened via the popup method, via JS
      if (!shouldUseRelay && window?.opener?.opener) {
        // 2/19/2023, shopify started rediercting to success by opening a different tab, so we have to go 1 layer down
        // The opener of this new tab (made by shopify) is the Rutter popup, and the opener of that is the Rutter link JS
        try {
          window.opener.opener.postMessage(
            JSON.stringify({
              publicToken: public_token,
              nonce,
              type: "SUCCESS",
            }),
            origin,
          );
        } catch (e) {
          captureException(e);
          setError("Unable to authenticate.");
        } finally {
          setLoading(false);
        }
      }

      if (!shouldUseRelay && window?.opener) {
        try {
          window.opener.postMessage(
            JSON.stringify({
              publicToken: public_token,
              nonce,
              type: "SUCCESS",
            }),
            origin,
          );
        } catch (e) {
          captureException(e);
          setError("Unable to authenticate.");
        } finally {
          setLoading(false);
        }
      }

      // If no window.opener, we are in a popup and need to use a backup method to communicate with parent JS
      if (shouldUseRelay) {
        axios
          .post(
            `${getBackendUrl()}/versioned/link/websocket/link_complete`,
            { nonce, public_token },
            {
              headers: {
                "Content-Type": "application/json",
                "X-Rutter-Version": "2023-02-07",
              },
            },
          )
          .then(() => setLoading(false))
          .catch((e) => {
            setError("Unable to authenticate.");
            setLoading(false);
            captureException(e);
          });

        // TODO(gustavo): Remove this part of code later when we know its safe
        try {
          const ws = new WebSocket(
            `${getBackendWsUrl()}/relay?channelId=${encodeURIComponent(
              nonce as string,
            )}&role=popup`,
          );
          ws.addEventListener("open", () => {
            ws.send(
              JSON.stringify({
                publicToken: public_token,
                nonce,
                type: "SUCCESS",
              }),
            );
            setLoading(false);
          });
          ws.addEventListener("close", () => {
            console.warn("popup: ws closed");
          });
        } catch (e) {
          captureException(e);
        }
      }
    }
    if (!public_token || !nonce || !origin) {
      setLoading(false);
    }
  }, []);

  // Auto-close once relay completes (loading flips false). Safety fallback at
  // 5s in case neither axios nor WS resolves (e.g. backend wedged) — better to
  // close than hang. Synchronous window.opener.postMessage path also flips
  // loading false in its finally block, so this fires near-immediately there.
  React.useEffect(() => {
    if (!shouldCloseImmediatelyGate({ orgId })) return;

    let closed = false;
    const close = () => {
      if (closed) return;
      closed = true;
      console.warn("popup: closing now");
      handleClose();
    };

    const fallback = setTimeout(() => {
      console.warn("popup: closing via 5s fallback");
      close();
    }, 5000);

    if (!loading) {
      // Small grace so postMessage / axios / ws bytes flush before window dies.
      const grace = setTimeout(close, 250);
      return () => {
        clearTimeout(fallback);
        clearTimeout(grace);
      };
    }

    return () => clearTimeout(fallback);
  }, [loading, orgId]);

  if (shouldCloseImmediatelyGate({ orgId })) {
    return (
      <div
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 99,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (isCapitalOneGate()) {
    return (
      <div
        style={{ fontFamily: "Optimist" }}
        className="grid grid-rows-[20px_1fr_20px] items-center justify-items-center min-h-screen p-8 pb-20 gap-16 sm:p-20 "
      >
        <main className="flex flex-col gap-8 row-start-2 items-center sm:items-start">
          {/* RUTTER CODE START ============== */}
          <div
            style={{ padding: "3.2rem", height: "100%", position: "relative" }}
          >
            <div className="flex flex-column items-center justify-center pb-4"></div>

            <div className="flex flex-column items-center justify-center pb-4">
              <img
                style={{ height: 80, marginBottom: 20 }}
                alt=""
                src="/capitalone_grv-spot-checkmark.svg"
              ></img>
            </div>

            <div className="text-xl text-center">
              <div className="text-center text-[24px] ">
                You've successfully connected your account
              </div>
            </div>

            <div className="mt-8 w-2xl">
              <div>
                <div className="flex items-center">
                  <div>
                    {/* <div className="ml-3 text-lg">Secure</div> */}
                    <div className="ml-3 text-[16px]"></div>
                  </div>
                </div>
              </div>
            </div>

            <div
              style={{
                marginTop: "12px",
                marginBottom: ".8rem",
                marginRight: ".8rem",
                marginLeft: ".8rem",
              }}
              className=""
            >
              <div className="flex items-center justify-center flex-col ">
                {/* word-wrap breakword */}
                <p className="mt-3 max-w-sm text-center ">
                  <span
                    onClick={handleClose}
                    className="text-[#0070A8] font-semibold cursor-pointer"
                  >
                    Close this window
                  </span>
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-4 items-center flex-col sm:flex-row"></div>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 99,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Spin size="large" tip="Verifying credentials, please wait..." />
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 99,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Result
          status="error"
          title="Authentication failed!"
          subTitle=""
          extra={[
            <Button type="primary" key="console" onClick={handleClose}>
              Close window
            </Button>,
          ]}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 99,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Result
        status="success"
        title="Authentication Complete!"
        subTitle=""
        extra={[
          <Button type="primary" key="console" onClick={handleClose}>
            Close window
          </Button>,
        ]}
      />
    </div>
  );
}

export default LinkComplete;
